import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import LiveClassWidget from "../components/LiveClassWidget";
import { toast } from "sonner";
import { 
  BookOpen,
  PlayCircle,
  FileText,
  HelpCircle,
  ClipboardList,
  CheckCircle,
  Circle,
  Clock,
  ArrowLeft,
  ChevronRight
} from "lucide-react";

const CourseLearn = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseContent();
    fetchProgress();
  }, [courseId]);

  const fetchCourseContent = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/content`, { credentials: 'include' });
      if (response.ok) {
        setCourse(await response.json());
      } else {
        toast.error("Course not found");
        navigate('/courses');
      }
    } catch (error) {
      console.error("Error fetching course:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/progress`, { credentials: 'include' });
      if (response.ok) {
        setProgress(await response.json());
      }
    } catch (error) {
      console.error("Error fetching progress:", error);
    }
  };

  const markLessonComplete = async (lessonId) => {
    try {
      const response = await fetch(
        `${API}/courses/${courseId}/lessons/${lessonId}/progress?status=completed&progress_percent=100`,
        { method: 'POST', credentials: 'include' }
      );

      if (response.ok) {
        toast.success("Lesson completed!");
        fetchProgress();
        setCurrentLesson(null);
      }
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const getLessonIcon = (type) => {
    const icons = {
      video: PlayCircle,
      text: FileText,
      quiz: HelpCircle,
      assignment: ClipboardList
    };
    const Icon = icons[type] || FileText;
    return Icon;
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') {
      return <CheckCircle className="w-5 h-5 text-[#10B981]" />;
    } else if (status === 'in_progress') {
      return <Clock className="w-5 h-5 text-[#F59E0B]" />;
    }
    return <Circle className="w-5 h-5 text-slate-500" />;
  };

  const calculateOverallProgress = () => {
    let totalLessons = 0;
    let completedLessons = 0;
    
    progress.forEach(module => {
      module.lessons?.forEach(lesson => {
        totalLessons++;
        if (lesson.progress_status === 'completed') completedLessons++;
      });
    });
    
    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      </PageLayout>
    );
  }

  if (!course) return null;

  const overallProgress = calculateOverallProgress();

  // If viewing a specific lesson
  if (currentLesson) {
    return (
      <PageLayout>
        <div className="space-y-6" data-testid="lesson-view">
          {/* Lesson Header */}
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => setCurrentLesson(null)}
              className="text-slate-500 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Course
            </Button>
            <Button 
              onClick={() => markLessonComplete(currentLesson.lesson_id)}
              className="btn-primary"
              data-testid="mark-complete-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Complete
            </Button>
          </div>

          {/* Lesson Content */}
          <Card className="card-surface">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                {(() => {
                  const Icon = getLessonIcon(currentLesson.lesson_type);
                  return <Icon className="w-6 h-6 text-brand-navy" />;
                })()}
                <Badge className="badge-gold capitalize">{currentLesson.lesson_type}</Badge>
                {currentLesson.duration_minutes > 0 && (
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {currentLesson.duration_minutes} min
                  </span>
                )}
              </div>
              <CardTitle className="text-slate-900 text-2xl">{currentLesson.title}</CardTitle>
              {currentLesson.description && (
                <p className="text-slate-500 mt-2">{currentLesson.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Render content based on lesson type */}
              {currentLesson.lesson_type === 'video' && currentLesson.content?.video_url && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <iframe
                    src={currentLesson.content.video_url}
                    className="w-full h-full"
                    allowFullScreen
                    title={currentLesson.title}
                  />
                </div>
              )}

              {currentLesson.lesson_type === 'text' && (
                <div className="prose prose-invert max-w-none">
                  <div 
                    className="text-slate-600 leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: currentLesson.content?.html || currentLesson.content?.text || 'No content available.' }}
                  />
                </div>
              )}

              {currentLesson.lesson_type === 'quiz' && currentLesson.content?.questions && (
                <div className="space-y-6">
                  {currentLesson.content.questions.map((q, idx) => (
                    <div key={idx} className="p-4 bg-surface-muted rounded-lg">
                      <p className="font-medium text-slate-900 mb-3">{idx + 1}. {q.question}</p>
                      <div className="space-y-2">
                        {q.options?.map((opt, optIdx) => (
                          <label key={optIdx} className="flex items-center gap-3 p-3 bg-white rounded cursor-pointer hover:bg-[#1E3A8A]/20">
                            <input type="radio" name={`q_${idx}`} className="accent-[#FFD507]" />
                            <span className="text-slate-600">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {currentLesson.lesson_type === 'assignment' && (
                <div className="p-6 bg-surface-muted rounded-lg">
                  <h3 className="text-lg font-medium text-slate-900 mb-4">Assignment Instructions</h3>
                  <div className="text-slate-600 whitespace-pre-wrap">
                    {currentLesson.content?.instructions || 'No instructions available.'}
                  </div>
                  {currentLesson.content?.submission_link && (
                    <Button className="btn-primary mt-4">
                      Submit Assignment
                    </Button>
                  )}
                </div>
              )}

              {/* Default content display */}
              {!currentLesson.content || Object.keys(currentLesson.content).length === 0 && (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500">Lesson content coming soon.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  // Course overview with modules
  return (
    <PageLayout>
      <div className="space-y-6" data-testid="course-learn-page">
        {/* Course Header */}
        <div className="flex items-start justify-between">
          <div>
            <Link to="/courses" className="text-brand-navy text-sm hover:underline flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> All Courses
            </Link>
            <h1 className="text-3xl font-heading font-bold text-slate-900">{course.title}</h1>
            <p className="text-slate-500 mt-2">{course.description}</p>
          </div>
        </div>

        {/* Live Class join (course/cohort context) */}
        <LiveClassWidget endpoint="/me/live-class" variant="dashboard" />

        {/* Progress Card */}
        <Card className="card-surface">
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-slate-500">Your Progress</p>
                <p className="text-2xl font-bold text-slate-900">{overallProgress}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">
                  {progress.reduce((acc, m) => acc + (m.lessons?.filter(l => l.progress_status === 'completed').length || 0), 0)} of {progress.reduce((acc, m) => acc + (m.lessons?.length || 0), 0)} lessons completed
                </p>
              </div>
            </div>
            <Progress value={overallProgress} className="h-3 bg-surface-muted" />
          </CardContent>
        </Card>

        {/* Modules & Lessons */}
        <div className="space-y-4">
          {progress.length > 0 ? (
            <Accordion type="multiple" defaultValue={progress.map(m => m.module_id)} className="space-y-4">
              {progress.map((module, moduleIdx) => (
                <AccordionItem 
                  key={module.module_id} 
                  value={module.module_id}
                  className="card-surface border-0"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-brand-gold/20 rounded-full flex items-center justify-center text-brand-navy font-bold text-sm">
                        {moduleIdx + 1}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{module.title}</p>
                        {module.description && (
                          <p className="text-sm text-slate-500">{module.description}</p>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="space-y-2 mt-2">
                      {module.lessons?.map((lesson, lessonIdx) => {
                        const Icon = getLessonIcon(lesson.lesson_type);
                        return (
                          <button
                            key={lesson.lesson_id}
                            onClick={() => setCurrentLesson(lesson)}
                            className="w-full flex items-center justify-between p-4 bg-surface-muted rounded-lg hover:bg-[#1E3A8A]/20 transition-colors text-left"
                            data-testid={`lesson-${lesson.lesson_id}`}
                          >
                            <div className="flex items-center gap-4">
                              {getStatusIcon(lesson.progress_status)}
                              <Icon className="w-5 h-5 text-slate-500" />
                              <div>
                                <p className={`font-medium ${lesson.progress_status === 'completed' ? 'text-slate-500' : 'text-slate-900'}`}>
                                  {lesson.title}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <span className="capitalize">{lesson.lesson_type}</span>
                                  {lesson.duration_minutes > 0 && (
                                    <>
                                      <span>•</span>
                                      <span>{lesson.duration_minutes} min</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-500" />
                          </button>
                        );
                      })}
                      {(!module.lessons || module.lessons.length === 0) && (
                        <p className="text-center text-slate-500 py-4">No lessons in this module yet.</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Card className="card-surface">
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Content Yet</h3>
                <p className="text-slate-500">Course modules and lessons are being prepared.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default CourseLearn;
